import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ShoppingBag, X } from "lucide-react";
import { PRICE } from "./BuyIssue";
import { Spread } from "./FlipSpread";

const useWide = () => {
  const query = "(min-width: 768px)";
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
};

// Desktop shows real spreads: cover alone, then 2-3, 4-5 ... ; phones show one page at a time.
const toViews = (pages, wide) => {
  if (!wide) return pages.map((p, i) => [{ src: p, n: i + 1 }]);
  const views = [[null, { src: pages[0], n: 1 }]];
  for (let i = 1; i < pages.length; i += 2) {
    views.push([{ src: pages[i], n: i + 1 }, pages[i + 1] ? { src: pages[i + 1], n: i + 2 } : null]);
  }
  return views;
};

const EndCard = ({ total, onBuy }) => (
  <div className="w-full h-full bg-hot text-ink border-2 border-ink flex flex-col justify-between p-6 sm:p-10" data-testid="flipbook-end">
    <span className="display text-[5rem] sm:text-[8rem] leading-[0.8]">y</span>
    <div>
      <p className="display text-3xl sm:text-5xl leading-[0.9]">Liked the preview?</p>
      <p className="font-serif italic text-lg sm:text-xl mt-4 max-w-md">The rest of the issue{total ? `, all ${total} pages,` : ""} is waiting for you in the PDF, for {PRICE}.</p>
      <button type="button" onClick={onBuy} className="mt-6 inline-flex items-center gap-3 bg-ink text-cream kicker px-6 py-4 border-2 border-ink hover:bg-cream hover:text-ink transition-colors" data-testid="flipbook-buy-btn">
        <ShoppingBag className="h-4 w-4" /> Buy the PDF · {PRICE}
      </button>
    </div>
  </div>
);

const NavButton = ({ dir, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir < 0 ? "Previous page" : "Next page"}
    className="h-12 w-12 shrink-0 grid place-items-center border-2 border-cream text-cream hover:bg-hot hover:border-hot hover:text-ink transition-colors disabled:opacity-25 disabled:pointer-events-none"
    data-testid={dir < 0 ? "flipbook-prev-btn" : "flipbook-next-btn"}
  >
    {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

export const IssueFlipbook = ({ pages, onBuy, total, title, onClose }) => {
  const wide = useWide();
  const views = useMemo(() => toViews(pages, wide), [pages, wide]);
  const last = views.length; // index `last` is the end card
  const [at, setAt] = useState(0);
  const [flip, setFlip] = useState(null);
  const go = (d) => {
    if (flip) return;
    const to = Math.max(0, Math.min(last, at + d));
    if (to === at) return;
    if (to === last || at === last) return setAt(to); // the end card just fades in/out
    setFlip({ dir: d, from: at, to });
  };
  const done = () => {
    setAt(flip.to);
    setFlip(null);
  };

  useEffect(() => {
    setFlip(null);
    setAt((i) => Math.min(i, last));
  }, [last]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  });

  const view = views[at];
  const nums = view ? view.filter(Boolean).map((p) => p.n) : [];
  const label = view ? `${nums.join("–")} / ${pages.length}` : "End of preview";
  const frame = wide
    ? { aspectRatio: "420 / 297", height: "min(calc(100dvh - 180px), calc((100vw - 200px) * 297 / 420))" }
    : { aspectRatio: "210 / 297", height: "min(calc(100dvh - 200px), calc((100vw - 32px) * 297 / 210))" };

  return createPortal(
    <motion.div
      className="flip fixed inset-0 z-[100] bg-ink text-cream flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${title}`}
      data-testid="flipbook"
    >
      <div className="flex items-center justify-between gap-4 px-4 sm:px-8 h-16 border-b border-cream/20">
        <p className="kicker truncate"><span className="text-hot">Preview</span> · {title}</p>
        <div className="flex items-center gap-4">
          <span className="kicker text-cream/80 hidden sm:inline" data-testid="flipbook-counter">{label}</span>
          <button type="button" onClick={onClose} aria-label="Close preview" className="h-10 w-10 grid place-items-center border-2 border-cream hover:bg-hot hover:border-hot hover:text-ink transition-colors" data-testid="flipbook-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center gap-4 sm:gap-8 px-4 min-h-0">
        {wide && <NavButton dir={-1} disabled={at === 0} onClick={() => go(-1)} />}
        <motion.div
          className="relative [perspective:3200px]"
          style={frame}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.15}
          dragSnapToOrigin
          onDragEnd={(_, info) => {
            if (info.offset.x < -60) go(1);
            if (info.offset.x > 60) go(-1);
          }}
          data-testid="flipbook-stage"
        >
          {at === last ? (
            <motion.div className="absolute inset-0" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <EndCard total={total} onBuy={onBuy} />
            </motion.div>
          ) : (
            <Spread views={views} at={at} flip={flip} wide={wide} onDone={done} />
          )}
        </motion.div>
        {wide && <NavButton dir={1} disabled={at === last} onClick={() => go(1)} />}
      </div>

      <div className="h-20 flex items-center justify-center gap-6 px-4">
        {!wide && <NavButton dir={-1} disabled={at === 0} onClick={() => go(-1)} />}
        <span className="kicker text-cream/80 sm:hidden" data-testid="flipbook-counter-mobile">{label}</span>
        {!wide && <NavButton dir={1} disabled={at === last} onClick={() => go(1)} />}
        {wide && <p className="kicker text-cream/70">Use the arrows or drag the page</p>}
      </div>

      <div className="hidden">
        {[...(views[at + 1] || []), ...(views[at - 1] || [])].filter(Boolean).map((p) => <img key={p.n} src={p.src} alt="" />)}
      </div>
    </motion.div>,
    document.body
  );
};
